/**
 * Keyboard map (SPEC §6.3) for the spike. Edit mode handles keys on the textarea; selection mode
 * and idle handle them on the document.
 *
 * ⌥→/⌥← (zoom) clash with macOS word navigation inside text, so by default (Settings → Editor →
 * zoom keys) they zoom in edit mode only at the end/start of the text; ⌘./⌘, always zoom. With
 * 'option-arrows', ⌥→/⌥← zoom everywhere (SPEC §6.3 literal).
 */
import { caretColumn, caretOnEdgeLine } from './caret.ts'
import type { ClientStore } from './store.ts'

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)

interface Keyish {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
  preventDefault(): void
}

const modOf = (e: Keyish): boolean => (isMac ? e.metaKey : e.ctrlKey)

/** Returns true when the key was a command (and its default prevented). */
export function handleEditKey(store: ClientStore, ta: HTMLTextAreaElement, e: Keyish): boolean {
  const k = e.key
  const mod = modOf(e)
  const { altKey: alt, shiftKey: shift } = e
  const plain = !mod && !alt
  const vertical = k === 'ArrowUp' || k === 'ArrowDown'
  const up = k === 'ArrowUp'
  const run = (fn: () => void): true => {
    e.preventDefault()
    fn()
    return true
  }
  const arrowsZoom = store.options.zoomKeys === 'option-arrows'
  const caretAtStart = ta.selectionStart === 0 && ta.selectionEnd === 0
  const caretAtEnd = ta.selectionStart === ta.value.length && ta.selectionEnd === ta.value.length

  // An open picker owns ↑/↓, ⏎/Tab and Esc (SPEC §6.3: Esc closes the popover first).
  if (store.picker && !mod && !alt) {
    if (vertical && !shift) return run(() => store.movePicker(up ? -1 : 1))
    if ((k === 'Enter' || k === 'Tab') && !shift) return run(() => store.pickItem())
    if (k === 'Escape') return run(() => store.closePicker())
  }
  if (k === 'Enter' && mod && !alt && !shift) return run(() => store.cycleTask())
  if (k === 'Enter' && plain && !shift) return run(() => store.split())
  if (k === 'Backspace' && plain && !shift && caretAtStart) return run(() => store.merge())
  if (k === 'Tab' && plain) return run(() => shift ? store.outdent() : store.indent())
  if (vertical && alt && !mod && !shift) return run(() => store.move(up))
  if (vertical && mod && !alt && !shift) return run(() => store.collapse(up))
  if (mod && !alt && (k === '.' || k === ',')) return run(() => k === '.' ? store.zoomIn() : store.zoomOut())
  if (alt && !mod && !shift && k === 'ArrowRight' && (caretAtEnd || arrowsZoom)) return run(() => store.zoomIn())
  if (alt && !mod && !shift && k === 'ArrowLeft' && (caretAtStart || arrowsZoom)) return run(() => store.zoomOut())
  if (mod && !alt && k.toLowerCase() === 'z') return run(() => shift ? store.redo() : store.undo())
  if (mod && !alt && !shift && k.toLowerCase() === 'y' && !isMac) return run(() => store.redo())
  if (mod && !alt && !shift && k.toLowerCase() === 'a') {
    const all = ta.selectionStart === 0 && ta.selectionEnd === ta.value.length
    return all ? run(() => store.selectAll()) : false
  }
  if (mod && !alt && !shift && k.toLowerCase() === 'd') return run(() => store.duplicate())
  if (vertical && shift && !mod && !alt) {
    return caretOnEdgeLine(ta, up) ? run(() => store.startBlockSelection(up)) : false
  }
  if (vertical && plain && !shift) {
    if (ta.selectionStart !== ta.selectionEnd) return false
    return caretOnEdgeLine(ta, up) ? run(() => store.navigate(up, caretColumn(ta))) : false
  }
  if (k === 'Escape') return run(() => store.escape())
  return false
}

export function handleSelectKey(store: ClientStore, e: Keyish): boolean {
  const k = e.key
  const mod = modOf(e)
  const { altKey: alt, shiftKey: shift } = e
  const vertical = k === 'ArrowUp' || k === 'ArrowDown'
  const up = k === 'ArrowUp'
  const run = (fn: () => void): true => {
    e.preventDefault()
    fn()
    return true
  }
  if (mod && !alt && k.toLowerCase() === 'z') return run(() => shift ? store.redo() : store.undo())
  // With nothing to escape, Esc is left to the view (it zooms out of a node view).
  if (k === 'Escape' && store.mode.kind !== 'idle') return run(() => store.escape())
  if (store.mode.kind !== 'select') return false

  if (vertical && shift && !mod && !alt) return run(() => store.extendSelection(up))
  if (vertical && !shift && !mod && !alt) return run(() => store.moveSelection(up))
  if (vertical && alt && !mod) return run(() => store.move(up))
  if (vertical && mod && !alt) return run(() => store.collapse(up))
  if (k === 'Tab' && !mod && !alt) return run(() => shift ? store.outdent() : store.indent())
  if ((k === 'Backspace' || k === 'Delete') && !mod && !alt) return run(() => store.remove())
  if (mod && !alt && !shift && k.toLowerCase() === 'd') return run(() => store.duplicate())
  if (mod && !alt && !shift && k.toLowerCase() === 'a') return run(() => store.selectAll())
  if (k === 'Enter' && mod && !alt && !shift) return run(() => store.cycleTask())
  if ((alt && k === 'ArrowRight') || (mod && k === '.')) return run(() => store.zoomIn())
  if ((alt && k === 'ArrowLeft') || (mod && k === ',')) return run(() => store.zoomOut())
  if (k === 'Enter' && !mod && !alt && !shift) {
    const { head, root } = store.mode
    return run(() => store.edit(head, root))
  }
  return false
}
